// PRD.313 — generic longitudinal history surface.
//
// One visual control that reads as a single continuing clinical document:
// a bounded, scrollable read-only History card (oldest → newest) with the
// ACTIVE encounter's editable entry attached directly beneath it. Review above,
// append below.
//
// Deliberately DOMAIN-AGNOSTIC. It knows nothing about Medical History — it
// takes a label, a patient-record block, a list of dated read-only entries, and
// the current entry's editor as `children`. PRD.311's Specialty / Dental
// History can reuse it unchanged; PRD.313 explicitly does not implement those.
//
// It is a COMPOSED VIEW, never a storage model (PRD §5.4): each block keeps its
// own record underneath, and nothing here concatenates or writes anything.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { shouldRenderHistory } from './longitudinal-history-logic';

/**
 * Bounded height for the scrollable History card inside the Guided Note.
 * Inherits PRD.312's precedent (the same value TranscriptDrawer uses for
 * read-only clinical text) so long histories never stretch the Visit Note.
 */
const HISTORY_MAX_HEIGHT = 280;

/** One read-only historical entry. */
export interface LongitudinalEntry {
  /** Stable identity for React (an encounter id, typically). */
  id: string;
  /** Right-hand provenance/date label for the row. */
  label: string;
  /** The narrative. Callers filter out blanks before passing entries in. */
  text: string;
}

export interface LongitudinalHistoryEditorProps {
  /** Section name, used for accessible labelling (e.g. "Medical History"). */
  label: string;
  /** Baseline patient-record narrative, or null when absent. */
  patientRecord: string | null;
  /** Right-hand provenance label for `patientRecord`, e.g. "Patient Record". */
  patientRecordLabel?: string;
  /** Read-only historical entries, ALREADY ordered oldest → newest. */
  previousEntries: readonly LongitudinalEntry[];
  /**
   * Render the History card at all. When false the surface is just the current
   * editor, so it looks like an ordinary field rather than an empty historical
   * shell (PRD §9).
   */
  showHistory: boolean;
  /** The current encounter's editor. Owned and persisted by the caller. */
  children: ReactNode;
}

export function LongitudinalHistoryEditor({
  label,
  patientRecord,
  patientRecordLabel = 'Patient Record',
  previousEntries,
  showHistory,
  children,
}: LongitudinalHistoryEditorProps) {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // PRD §9 + §11.1 — off when the clinic disabled history, and off when there
  // is genuinely nothing to show, so the surface is never an empty historical
  // shell above the editor.
  const renderHistory = shouldRenderHistory(showHistory, patientRecord, previousEntries);

  // PRD §7.3 — open near the LATEST history, adjacent to the current entry.
  // A clinician documenting today should not have to scroll through years of
  // history first. Re-runs when the content changes so a late-arriving sync
  // does not strand the viewport mid-history.
  const entryCount = previousEntries.length;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [renderHistory, entryCount, patientRecord]);

  return (
    <>
      <section
        aria-label={label}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {renderHistory && (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-surface)',
              overflow: 'hidden',
              // Sits directly above the editor so the two read as one grouped
              // surface: review above, append below.
              marginBottom: 'var(--sp-2)',
              // The history card stays sized to its (already bounded) content;
              // the editor beneath it takes any remaining height.
              flexShrink: 0,
            }}
          >
            <header
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--sp-2)',
                padding: 'var(--sp-3) var(--sp-4)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                History
              </span>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-primary, #2563eb)',
                  cursor: 'pointer',
                }}
              >
                View All
              </button>
            </header>

            {/* Scrolls independently of the editor below, so the editor stays
                put and reachable however long the history gets — no sticky
                positioning, and nothing to trap focus. */}
            <div
              ref={scrollRef}
              tabIndex={0}
              role="group"
              aria-label={`${label} — previous entries, read only`}
              style={{ maxHeight: HISTORY_MAX_HEIGHT, overflowY: 'auto' }}
            >
              <HistoryRows
                patientRecord={patientRecord}
                patientRecordLabel={patientRecordLabel}
                previousEntries={previousEntries}
              />
            </div>
          </div>
        )}

        {/* Current entry — always the final block, always in the same slot in
            this tree so toggling `showHistory` never remounts the caller's
            editor (which would drop unsaved text and fire its unmount flush).
            No wrapper padding: the editor owns the full width of the surface,
            and its own textarea padding keeps the text readable. */}
        {children}
      </section>

      {/* View All — the established shared Drawer (focus trap, Esc, scroll
          lock). READ-ONLY history only: duplicating the editor here would mean
          a second editing state that could diverge from the live one, which
          PRD §7.4 rules out. The current entry stays editable in the surface
          behind it. */}
      <Drawer
        open={expanded}
        onOpenChange={setExpanded}
        title={label}
        description="Read-only history from the patient record and earlier visits. Today's entry is edited in the visit note."
        width={520}
      >
        <div style={{ padding: 'var(--sp-5, 20px)' }}>
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              overflow: 'hidden',
            }}
          >
            <HistoryRows
              patientRecord={patientRecord}
              patientRecordLabel={patientRecordLabel}
              previousEntries={previousEntries}
              spacious
            />
          </div>
        </div>
      </Drawer>
    </>
  );
}

// ─── History rows (shared by the bounded card and the expanded drawer) ───

function HistoryRows({
  patientRecord,
  patientRecordLabel,
  previousEntries,
  spacious = false,
}: {
  patientRecord: string | null;
  patientRecordLabel: string;
  previousEntries: readonly LongitudinalEntry[];
  spacious?: boolean;
}) {
  // PRD §11.3/§11.4 — each source appears only when it has content; no empty
  // "Patient Record" or "Previous Visits" placeholders.
  const rows: { key: string; text: string; meta: string }[] = [];
  if (patientRecord) {
    rows.push({ key: '__patient_record__', text: patientRecord, meta: patientRecordLabel });
  }
  for (const entry of previousEntries) {
    rows.push({ key: entry.id, text: entry.text, meta: entry.label });
  }

  return (
    <>
      {rows.map((row, i) => (
        <Row
          key={row.key}
          text={row.text}
          meta={row.meta}
          spacious={spacious}
          divider={i < rows.length - 1}
        />
      ))}
    </>
  );
}

/**
 * One read-only historical row: narrative on the left, provenance/date on the
 * right.
 *
 * Rendered as plain text, never a disabled textarea (PRD §6.3): it carries no
 * input affordance, so it cannot be mistaken for something editable, while
 * staying selectable and copyable. The right-hand meta ("Patient Record", a
 * past date) carries the read-only meaning, so the distinction never rests on
 * color alone (PRD §13.2).
 */
function Row({
  text,
  meta,
  spacious,
  divider,
}: {
  text: string;
  meta: string;
  spacious: boolean;
  divider: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        padding: spacious ? 'var(--sp-3) var(--sp-4)' : '10px var(--sp-4)',
        borderBottom: divider ? '1px solid var(--border)' : undefined,
      }}
    >
      <div
        aria-readonly="true"
        aria-label={`${meta} (read only)`}
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          minWidth: 0,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          lineHeight: 'var(--line-height-body)',
          userSelect: 'text',
        }}
      >
        {text}
      </div>
      <div
        style={{
          flexShrink: 0,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          whiteSpace: 'nowrap',
        }}
      >
        {meta}
      </div>
    </div>
  );
}
